/**
 * AddSectionCard Component
 *
 * Clickable card/divider to add new sections to a template.
 * Appears between sections and at the end of the sections list.
 */

import React from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface AddSectionCardProps {
  /** Click handler to open section selector */
  onClick: () => void;
  /** Optional label override */
  label?: string;
}

export const AddSectionCard: React.FC<AddSectionCardProps> = ({
  onClick,
  label = 'ADD SECTION',
}) => {
  const theme = useTheme();

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 2,
        cursor: 'pointer',
        position: 'relative',
        '&:hover': {
          '& .add-section-label': {
            color: 'primary.main',
            bgcolor: 'primary.50',
            borderColor: 'primary.main',
          },
          '& .add-section-line': {
            borderColor: 'primary.main',
          },
        },
        '&:focus-visible': {
          outline: 'none',
          '& .add-section-label': {
            color: 'primary.main',
            bgcolor: 'primary.50',
            borderColor: 'primary.main',
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
          },
        },
      }}
    >
      {/* Dashed line */}
      <Box
        className="add-section-line"
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          borderTop: '2px dashed',
          borderColor: 'divider',
          transition: theme.transitions.create('border-color', {
            duration: theme.transitions.duration.short,
          }),
        }}
      />

      {/* Label badge */}
      <Box
        className="add-section-label"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          position: 'relative',
          zIndex: 1,
          transition: theme.transitions.create(
            ['color', 'background-color', 'border-color', 'box-shadow'],
            { duration: theme.transitions.duration.short },
          ),
        }}
      >
        <AddIcon
          sx={{
            fontSize: 16,
            color: 'inherit',
          }}
        />
        <Typography
          variant="caption"
          sx={{fontWeight: 600, letterSpacing: "0.5px",  color: 'inherit' }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};
